import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './http';
import { keys } from './queryKeys';

export function useMe() {
  return useQuery({ queryKey: keys.me, queryFn: api.me, retry: false });
}

export function useBots() {
  return useQuery({ queryKey: keys.bots, queryFn: api.listBots });
}

export function useBot(botId: string | undefined) {
  return useQuery({
    queryKey: keys.bot(botId ?? ''),
    queryFn: () => api.getBot(botId!),
    enabled: Boolean(botId),
  });
}

export function useChannels(botId: string | undefined) {
  return useQuery({
    queryKey: keys.channels(botId ?? ''),
    queryFn: () => api.listChannels(botId!),
    enabled: Boolean(botId),
  });
}

export function useSubscriptions(botId: string | undefined, buid: string | undefined) {
  return useQuery({
    queryKey: keys.subscriptions(botId ?? '', buid ?? ''),
    queryFn: () => api.getSubscriptions(botId!, buid!),
    enabled: Boolean(botId && buid),
  });
}

export function useChannelScopes(botId: string | undefined, buid: string | undefined) {
  return useQuery({
    queryKey: keys.scopes(botId ?? '', buid ?? ''),
    queryFn: () => api.getChannelScopes(botId!, buid!),
    enabled: Boolean(botId && buid),
  });
}

export function useFeatures(botId: string | undefined, buid: string | undefined) {
  return useQuery({
    queryKey: keys.features(botId ?? '', buid ?? ''),
    queryFn: () => api.getFeatures(botId!, buid!),
    enabled: Boolean(botId && buid),
  });
}

export function useEnrollments(botId: string | undefined) {
  return useQuery({
    queryKey: keys.enrollments(botId ?? ''),
    queryFn: () => api.listEnrollments(botId!),
    enabled: Boolean(botId),
  });
}

export function useChannelApiKeys(botId: string | undefined, buid: string | undefined) {
  return useQuery({
    queryKey: keys.channelKeys(botId ?? '', buid ?? ''),
    queryFn: () => api.listChannelApiKeys(botId!, buid!),
    enabled: Boolean(botId && buid),
  });
}

export function useEventSubCatalog() {
  return useQuery({ queryKey: keys.eventsubCatalog, queryFn: api.eventSubCatalog, staleTime: Infinity });
}

export function useHelixCatalog() {
  return useQuery({ queryKey: keys.helixCatalog, queryFn: api.helixCatalog, staleTime: Infinity });
}

export function useHistory(botId?: string, buid?: string) {
  return useQuery({
    queryKey: keys.history(botId, buid),
    queryFn: () => api.listHistory({ botId, broadcasterUserId: buid, limit: 200, kinds: 'eventsub,helix' }),
  });
}

export function useActivityLogs(botId?: string, buid?: string) {
  return useQuery({
    queryKey: keys.logs(botId, buid),
    queryFn: () => api.listLogs({ botId, broadcasterUserId: buid, limit: 200 }),
  });
}

export function useEventsubList(botId?: string) {
  return useQuery({
    queryKey: [...keys.eventsub, botId ?? ''],
    queryFn: () => api.listEventsub(botId),
  });
}

function invalidateBot(qc: ReturnType<typeof useQueryClient>, botId?: string) {
  void qc.invalidateQueries({ queryKey: keys.bots });
  if (botId) void qc.invalidateQueries({ queryKey: keys.bot(botId) });
}

export function useBotLifecycle(botId: string) {
  const qc = useQueryClient();
  const start = useMutation({
    mutationFn: () => api.startBot(botId),
    onSuccess: () => invalidateBot(qc, botId),
  });
  const stop = useMutation({
    mutationFn: () => api.stopBot(botId),
    onSuccess: () => invalidateBot(qc, botId),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteBot(botId),
    onSuccess: () => invalidateBot(qc),
  });
  return { start, stop, remove };
}

export function useRevokeChannel(botId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (buid: string) => api.revokeChannel(botId, buid),
    onSuccess: () => {
      invalidateBot(qc, botId);
      void qc.invalidateQueries({ queryKey: keys.channels(botId) });
    },
  });
}

export function useSaveSubscriptions(botId: string, buid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (types: Array<{ type: string; version: string }>) => api.setSubscriptions(botId, buid, types),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.subscriptions(botId, buid) });
      invalidateBot(qc, botId);
    },
  });
}

export function useSaveFeatures(botId: string, buid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (features: Array<{ featureId: string; desiredState: string }>) =>
      api.setFeatures(botId, buid, features),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.features(botId, buid) }),
  });
}

export function useCreateEnrollment(botId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.createEnrollment>[1]) => api.createEnrollment(botId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.enrollments(botId) }),
  });
}

export function useChannelReauth(botId: string, buid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { scopes?: string[] } = {}) => api.createChannelReauth(botId, buid, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.enrollments(botId) }),
  });
}

export function useRevokeEnrollment(botId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.revokeEnrollment,
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.enrollments(botId) }),
  });
}

export function useCreateChannelApiKey(botId: string, buid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { label: string; helixPermission?: 'read' | 'write'; eventTypes?: string[] }) =>
      api.createChannelApiKey(botId, buid, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.channelKeys(botId, buid) }),
  });
}

export function useRevokeApiKey(botId: string, buid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.revokeApiKey,
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.channelKeys(botId, buid) }),
  });
}

export function useReconcile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ botId, buid }: { botId?: string; buid?: string }) => api.reconcileEventSub(botId, buid),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.bots });
      void qc.invalidateQueries({ queryKey: keys.eventsub });
    },
  });
}
